/* Scoped traffic-page routing for Luvano Paysight sessions. */
(function (scope) {
  'use strict';

  const sessionEndpoint = '/app/api/integrations/paysight/sessions';
  const checkoutPage = 'https://tryluvano.co/pages/luvano-checkout';
  const trafficPaths = new Set([
    '/products/luvano-ceylon-cinnamon',
    '/products/luvano-cozy-crescent-pillow',
    '/products/luvano-little-explorer-backpack-pillow',
  ]);
  const rootReturnPaths = {
    'luvanosupps.com': '/products/luvano-ceylon-cinnamon',
    'luvanobaby.com': '/products/luvano-little-explorer-backpack-pillow',
    'luvanopets.co': '/products/luvano-cozy-crescent-pillow',
  };
  const whopScopes = {
    '/products/luvano-cozy-crescent-pillow': 'biz_RYJaIleHvG04vx',
    '/products/luvano-little-explorer-backpack-pillow': 'biz_6mkIEvJjRt8hxb',
    '/products/luvano-ceylon-cinnamon': 'biz_pWxretBoRizmt3',
  };
  const attributionKeys = new Set([
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id',
    'fbclid', 'gclid', 'gbraid', 'wbraid', 'ttclid', 'msclkid', 'li_fat_id',
    'twclid', 'rdt_cid', 'sccid', 'trafficSource',
    'wacid', 'wasid', 'waid', 'utm_meta_ad_id', 'utm_meta_adset_id', 'utm_meta_campaign_id',
  ]);
  const landingStorageKey = 'lv_whop_landing_url_v1';

  function whopVisitorId() {
    let value = metaCookie('_wuid');
    if (!value) {
      try { value = scope.localStorage?.getItem('_wuid'); } catch (_error) { /* Storage can be blocked. */ }
    }
    return typeof value === 'string' && value.length <= 256 && /^[A-Za-z0-9._~-]+$/.test(value)
      ? value : null;
  }

  function whopLandingUrl() {
    let current;
    try { current = new URL(scope.location.href); } catch (_error) { return null; }
    if (current.protocol !== 'https:' || current.origin !== scope.location.origin) return null;
    current.hash = '';
    for (const key of [...current.searchParams.keys()]) {
      if (!attributionKeys.has(key)) current.searchParams.delete(key);
    }
    let stored;
    try { stored = new URL(scope.sessionStorage?.getItem(landingStorageKey) || ''); }
    catch (_error) { /* No stored landing URL. */ }
    if (stored?.origin !== current.origin || stored.username || stored.password ||
        stored.protocol !== 'https:') stored = null;
    if (stored) {
      stored.hash = '';
      for (const key of [...stored.searchParams.keys()]) {
        if (!attributionKeys.has(key)) stored.searchParams.delete(key);
      }
      if (stored.toString().length > 2048 ||
          ![...stored.searchParams.keys()].some(key => attributionKeys.has(key))) stored = null;
    }
    const attributed = [...current.searchParams.keys()].some(key => attributionKeys.has(key));
    if (attributed && !stored && current.toString().length <= 2048) {
      try { scope.sessionStorage?.setItem(landingStorageKey, current.toString()); }
      catch (_error) { /* The current landing URL is still available. */ }
      stored = current;
    }
    return stored?.toString() || (attributed && current.toString().length <= 2048 ? current.toString() : null);
  }

  // Scope events on each traffic page to its own Whop business.
  const familyScope = whopScopes[returnPathForLocation()];
  if (familyScope && scope.whop?.setScope) {
    try { scope.whop.setScope(familyScope); } catch (_error) { /* Session routing still works. */ }
  }
  whopLandingUrl();

  function metaCookie(name) {
    const pair = (scope.document?.cookie || '').split(';').map(value => value.trim())
      .find(value => value.startsWith(name + '='));
    if (!pair) return null;
    let value;
    try { value = decodeURIComponent(pair.slice(name.length + 1)); }
    catch (_error) { return null; }
    return value.length <= 512 && /^[A-Za-z0-9._~-]+$/.test(value) ? value : null;
  }

  function returnPathForLocation() {
    const path = scope.location.pathname;
    const oldRootPath = rootReturnPaths[scope.location.hostname];
    if (oldRootPath) return path === '/' ||
      (scope.location.hostname === 'luvanopets.co' && path === oldRootPath)
      ? oldRootPath : null;
    return trafficPaths.has(path) ? path : null;
  }

  let pending = null;
  let redirecting = false;
  let checkoutDestination = null;

  function redirectToCheckout() {
    redirecting = true;
    try { scope.location.assign(checkoutDestination); }
    catch (_error) {
      redirecting = false;
      throw new Error('Checkout is temporarily unavailable. Please try again.');
    }
  }

  async function startSession(cart) {
    const returnPath = returnPathForLocation();
    if (!returnPath) throw new Error('Checkout is temporarily unavailable. Please try again.');
    const payload = { cart, returnPath, checkoutVersion: 2 };
    if (typeof scope.token === 'string' && scope.token.length <= 512) payload.eventToken = scope.token;
    const fbp = metaCookie('_fbp');
    const fbc = metaCookie('_fbc');
    if (fbp) payload.fbp = fbp;
    if (fbc) payload.fbc = fbc;
    const visitorId = whopVisitorId();
    const landingUrl = whopLandingUrl();
    if (visitorId) payload.whopVisitorId = visitorId;
    if (landingUrl) payload.whopLandingUrl = landingUrl;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(sessionEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('session');
      const result = await response.json();
      if (!/^[A-Za-z0-9_-]{10,50}$/.test(result.sessionId || '')) throw new Error('session');
      const url = new URL(checkoutPage);
      url.searchParams.set('s', result.sessionId);
      checkoutDestination = url.toString();
      try {
        const pixel = scope.whop;
        (pixel?.scope?.(whopScopes[returnPath]) || pixel)?.track?.('initiate_checkout');
      } catch (_error) { /* Tracking must not block checkout. */ }
      redirectToCheckout();
    } catch (_error) {
      throw new Error('Checkout is temporarily unavailable. Please try again.');
    } finally {
      clearTimeout(timeout);
    }
  }

  function start(cart) {
    if (redirecting) return Promise.resolve();
    if (pending) return pending;
    pending = (checkoutDestination ? Promise.resolve().then(redirectToCheckout) : startSession(cart))
      .finally(() => { pending = null; });
    return pending;
  }

  scope.LuvanoPaysight = { enabled: true, start };
})(window);

/* Runs in the head-loaded Paysight start asset before Shopify's delayed inline pixel. */
(function(w,d){
  "use strict";
  var pages={
    "luvanosupps.com":{scope:"biz_pWxretBoRizmt3",variants:{"48370856132795":1,"48370856165563":1,"48370856198331":1}},
    "luvanobaby.com":{scope:"biz_6mkIEvJjRt8hxb",variants:{"48370855805115":1,"48370855837883":1,"48370855870651":1,"48370855903419":1,"48370855936187":1}},
    "luvanopets.co":{scope:"biz_RYJaIleHvG04vx",variants:{"48370028904635":1,"48370028937403":1,"48370028970171":1}}
  };
  var page=pages[w.location.hostname];
  if(!page||w.location.pathname!=="/"||w.__lvWhopStorefrontInstalled)return;
  /* The existing inline pixel also checks this flag, so only one observer sends events. */
  w.__lvWhopStorefrontInstalled=true;
  var whop=w.whop;
  if(!whop){
    whop=w.whop={q:[],t:+new Date,s:[],o:"https://t.whop.tw",
      track:function(){whop.q.push([+new Date].concat([].slice.call(arguments)))},
      setScope:function(){whop.s=[].slice.call(arguments).filter(function(x){return typeof x==="string"});whop.q.push([+new Date,"setScope"].concat(whop.s))},
      scope:function(){var scope=[].slice.call(arguments);return{track:function(){whop.q.push([+new Date].concat([].slice.call(arguments)).concat([{__scope:scope}]))}}}
    };
    var script=d.createElement("script");script.async=true;script.src="https://t.whop.tw/s.js";
    var first=d.getElementsByTagName("script")[0];first.parentNode.insertBefore(script,first);
  }
  whop.setScope(page.scope);
  whop.track("page");
  whop.track("view_content");

  var nativeFetch=w.fetch;
  if(typeof nativeFetch!=="function")return;
  var cartState=null,queue=Promise.resolve();
  function id(item){return String(item&&(item.variant_id!=null?item.variant_id:item.id!=null?item.id:"")||"")}
  function quantity(cart){
    var out=Object.create(null),items=cart&&Array.isArray(cart.items)?cart.items:[];
    items.forEach(function(item){var key=id(item);if(page.variants[key])out[key]=(out[key]||0)+Math.max(0,Number(item.quantity)||0)});
    return out;
  }
  function increased(before,after){
    if(!before)return false;
    for(var key in after)if(after[key]>(before[key]||0))return true;
    return false;
  }
  function requestInfo(input,init){
    var raw=typeof input==="string"?input:input&&(input.url||input.href)||"";
    var method=String(init&&init.method||input&&input.method||"GET").toUpperCase();
    try{var url=new URL(raw,w.location.href);return {path:url.origin===w.location.origin?url.pathname:"",method:method}}
    catch(_){return {path:"",method:method}}
  }
  function requestedFamily(init){
    if(!init||typeof init.body!=="string")return null;
    try{
      var data=JSON.parse(init.body),items=Array.isArray(data&&data.items)?data.items:data&&data.id!=null?[data]:[];
      return items.length?items.some(function(item){return !!page.variants[id(item)]}):null;
    }catch(_){return null}
  }
  function read(response){return response&&response.ok&&typeof response.clone==="function"?response.clone().json():Promise.resolve(null)}
  var baseline;
  try{
    baseline=Promise.resolve(nativeFetch.call(w,"/cart.js",{credentials:"same-origin",headers:{Accept:"application/json"}}))
      .then(read).then(function(cart){if(cart)cartState=quantity(cart)}).catch(function(){});
  }catch(_){baseline=Promise.resolve()}
  w.fetch=function(input,init){
    var info=requestInfo(input,init),result=nativeFetch.apply(this,arguments),response=Promise.resolve(result);
    if(info.method==="POST"&&info.path==="/cart/add.js"){
      var requested=requestedFamily(init);
      queue=queue.then(function(){return baseline}).then(function(){return response}).then(function(r){
        if(!r||!r.ok)return;
        if(requested===true)whop.track("add_to_cart");
        return read(r).then(function(data){
          var items=data&&Array.isArray(data.items)?data.items:data&&(data.id!=null||data.variant_id!=null)?[data]:[];
          if(requested===null&&items.some(function(item){return !!page.variants[id(item)]}))whop.track("add_to_cart");
          if(cartState)items.forEach(function(item){var key=id(item);if(page.variants[key])cartState[key]=(cartState[key]||0)+Math.max(0,Number(item.quantity)||0)});
        });
      }).catch(function(){});
    }else if(info.method==="POST"&&info.path==="/cart/update.js"){
      queue=queue.then(function(){return baseline}).then(function(){return response}).then(read).then(function(cart){
        if(!cart)return;
        var next=quantity(cart),added=increased(cartState,next);cartState=next;
        if(added)whop.track("add_to_cart");
      }).catch(function(){});
    }else if(info.method==="POST"&&(info.path==="/cart/change.js"||info.path==="/cart/clear.js")){
      queue=queue.then(function(){return baseline}).then(function(){return response}).then(read).then(function(cart){if(cart)cartState=quantity(cart)}).catch(function(){});
    }
    return result;
  };
})(window,window.document);
